package pl.decks.posthog.client

import pl.decks.posthog.config.PostHogConfig
import pl.decks.posthog.external.posthog
import pl.decks.posthog.fromDynamic
import pl.decks.posthog.toDynamic

@Suppress("unused")
object PostHogClient {

    fun init(apiKey: String, config: PostHogConfig = PostHogConfig()) =
        posthog.init(apiKey, config.toDynamicConfig())

    inline fun <reified P> register(properties: P) =
        posthog.register(properties.toDynamic())

    fun register(properties: Map<String, String>) {
        val obj = js("{}")
        properties.forEach { (key, value) -> obj[key] = value }
        posthog.register(obj)
    }

    fun register(vararg properties: Pair<String, String>) {
        val obj = js("{}")
        properties.forEach { (key, value) -> obj[key] = value }
        posthog.register(obj)
    }

    fun unregister(property: String) =
        posthog.unregister(property)

    inline fun <reified E : PostHogEvent> capture(event: E) =
        posthog.capture(event.name, event.toDynamic())

    inline fun <reified P> identify(distinctId: String, properties: P) =
        posthog.identify(distinctId, properties.toDynamic())

    fun identify(distinctId: String) =
        posthog.identify(distinctId)

    fun reset() = posthog.reset()
    fun optIn() = posthog.optInCapturing()
    fun optOut() = posthog.optOutCapturing()

    fun getFeatureFlag(key: String): FeatureFlagValue {
        val resp = posthog.getFeatureFlag(key)
        return FeatureFlagValue.fromJS(resp)
    }

    inline fun <reified T> getFeatureFlagPayload(key: String): T? {
        val resp = posthog.getFeatureFlagPayload(key)
        return if (jsTypeOf(resp) == "undefined") null else fromDynamic<T>(resp)
    }

    fun isEnabled(key: String): Boolean {
        val resp = posthog.isFeatureEnabled(key)
        return if (jsTypeOf(resp) == "undefined") false else resp as Boolean
    }

    fun onFeatureFlags(callback: () -> Unit) =
        posthog.onFeatureFlags { _, _, _ -> callback() }

    fun onFeatureFlags(callback: (errorsLoading: Boolean) -> Unit) =
        posthog.onFeatureFlags { _, _, meta ->
            callback(meta?.errorsLoading as? Boolean ?: false)
        }

    fun onFeatureFlags(
        callback: (
            flags: List<String>,
            variants: Map<String, FeatureFlagValue>,
            errorsLoading: Boolean,
        ) -> Unit
    ) =
        posthog.onFeatureFlags { flags, variants, meta ->
            val flagList = (flags as? Array<String>)?.toList() ?: emptyList()

            val variantMap = js("Object.keys").call(null, variants)
                .unsafeCast<Array<String>>().associateWith { key ->
                    FeatureFlagValue.fromJS(variants[key])
                }

            val errors = meta?.errorsLoading as? Boolean ?: false
            callback(flagList, variantMap, errors)
        }
}
